import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Command, X, Shield, Users, Layers } from 'lucide-react';

// --- Internal Hooks & Components ---
import { useOmniSearch } from '../hooks/useOmniSearch';
import { SearchResultGroup } from './SearchResultGroup';
import { SearchEmptyState } from './SearchEmptyState';
import { AppState, DataScope } from '@/types';

interface SearchPortalProps {
  isOpen: boolean;
  onClose: () => void;
  state: AppState;
}

export const SearchPortal: React.FC<SearchPortalProps> = ({ isOpen, onClose, state }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  
  // 🛡️ HYBRID SCOPE STATE
  // Defaults to 'BOTH' if a household is active, otherwise locks to 'PERSONAL'
  const [activeScope, setActiveScope] = useState<DataScope>(
    state.currentHouseholdId ? 'BOTH' : 'PERSONAL'
  );

  // 🔍 🧠 OMNI-SEARCH LOGIC (Updated to accept activeScope)
  const { total, grouped } = useOmniSearch(query, state, activeScope);

  // 🕹️ KEYBOARD ORCHESTRATION
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // 🚀 NAVIGATION HANDLER
  const handleSelect = useCallback((item: any) => {
    onClose();
    setQuery('');
    
    switch (item.type) {
      case 'event': navigate('/calendar'); break;
      case 'task': navigate('/tasks'); break;
      case 'maintenance': navigate('/maintenance'); break;
      default: break;
    }
  }, [navigate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center pt-[15vh] p-4 bg-slate-900/40 dark:bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="absolute inset-0" onClick={onClose} />
      
      <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden relative z-10 animate-in zoom-in-95 duration-200">
        
        {/* ⌨️ INPUT SECTION & SCOPE TOGGLE */}
        <div className="flex items-center px-8 py-6 border-b border-slate-50 dark:border-slate-800/50 gap-4">
          <Search className={`w-6 h-6 transition-colors ${query ? 'text-brand' : 'text-slate-300'}`} />
          <input 
            autoFocus
            className="flex-1 bg-transparent border-none outline-none text-xl font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700"
            placeholder={activeScope === 'PERSONAL' ? t('search.placeholder_personal', 'Search personal partition...') : t('search.placeholder', 'Search household registry...')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {/* 🛡️ HYBRID SCOPE PIVOT */}
          {state.currentHouseholdId && (
            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
              {(['PERSONAL', 'SHARED', 'BOTH'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setActiveScope(s)}
                  className={`p-2 rounded-lg transition-all ${activeScope === s ? 'bg-white dark:bg-slate-700 shadow-sm text-brand' : 'text-slate-400 hover:text-slate-500'}`}
                  title={t(`search.scope.${s.toLowerCase()}`)}
                >
                  {s === 'PERSONAL' ? <Shield className="w-3.5 h-3.5" /> : s === 'SHARED' ? <Users className="w-3.5 h-3.5" /> : <Layers className="w-3.5 h-3.5" />}
                </button>
              ))}
            </div>
          )}

          <button onClick={onClose} className="p-2 bg-slate-50 dark:bg-slate-800 rounded-xl text-slate-400 hover:text-slate-600 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 📋 RESULTS SECTION */}
        <div className="max-h-[60vh] overflow-y-auto p-4 no-scrollbar min-h-[100px]">
          {query.length > 0 ? (
            total > 0 ? (
              <div className="space-y-2">
                <SearchResultGroup 
                  title={t('search.groups.calendar', 'Events')} 
                  type="calendar" 
                  results={grouped.calendar} 
                  query={query}
                  onSelect={handleSelect}
                  activeScope={activeScope}
                />
                <SearchResultGroup 
                  title={t('search.groups.tasks', 'Workstreams')} 
                  type="tasks" 
                  results={grouped.tasks} 
                  query={query}
                  onSelect={handleSelect}
                  activeScope={activeScope}
                />
                <SearchResultGroup 
                  title={t('search.groups.maintenance', 'Infrastructure')} 
                  type="maintenance" 
                  results={grouped.maintenance} 
                  query={query}
                  onSelect={handleSelect}
                  activeScope={activeScope}
                />
              </div>
            ) : (
              <SearchEmptyState 
                query={query} 
                currentHouseholdId={state.currentHouseholdId}
                activeScope={activeScope}
                onQuickCreate={(scope) => console.log(`Spawning ${scope} Creator...`)} 
              />
            )
          ) : (
            /* 💤 DEFAULT / IDLE STATE */
            <div className="py-20 text-center flex flex-col items-center gap-4">
              <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-[2rem]">
                {activeScope === 'PERSONAL' ? (
                  <Shield className="w-8 h-8 text-emerald-500/30" />
                ) : (
                  <Command className="w-8 h-8 text-slate-200 dark:text-slate-700" />
                )}
              </div>
              <div className="space-y-1">
                <p className="text-2xs font-black uppercase tracking-[0.3em] text-slate-300 dark:text-slate-600">
                  {activeScope === 'PERSONAL' ? t('search.hint.personal', 'Personal Index Active') : t('search.hint.main', 'Everyday Omni-Search')}
                </p>
                <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase italic">
                  {t('search.hint.sub', 'Indexing through')} {activeScope.toLowerCase()} {t('search.hint.context', 'context...')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ⌨️ KEYBOARD FOOTER */}
        <div className="px-8 py-4 bg-slate-50/50 dark:bg-slate-950/30 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[8px] font-black text-slate-500 shadow-sm">ESC</span>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{t('search.keys.close', 'to close')}</span>
            </div>
            {activeScope !== 'PERSONAL' && (
              <div className="flex items-center gap-1.5 opacity-50">
                <Users className="w-2.5 h-2.5 text-brand" />
                <span className="text-[8px] font-black uppercase tracking-widest text-slate-400">{t('search.status.shared', 'Shared Results Included')}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
             <div className={`w-1.5 h-1.5 rounded-full ${activeScope === 'PERSONAL' ? 'bg-emerald-500' : 'bg-indigo-500'}`} />
             <span className="text-[8px] font-black uppercase text-slate-400 tracking-widest">
               {activeScope === 'PERSONAL' ? 'Privacy Partition' : 'Hybrid Index'}
             </span>
          </div>
        </div>
      </div>
    </div>
  );
};